import type { Task } from "@app/core";
import { fromIso } from "@app/core";
import { Button, Calendar, Card, Clock, Flag, Repeat, cn } from "@app/ui";
import { memo, useState } from "react";

import type { I18nKey } from "../../lib/language.js";
import { ShowMoreFooter } from "../../lib/paged-list.js";
import { AnimatePresence, motion, springs, useReducedMotion } from "../../lib/motion/index.js";
import { useAppLocale } from "../../lib/useAppLocale.js";
import { useI18n } from "../../lib/useI18n.js";
import { useTimeFormat } from "../../lib/useTimeFormat.js";
import { formatDuration } from "../../lib/format.js";
import { agendaDayLabel, overdueAgeDays } from "../agenda/agenda.js";
import { matrixColorHex, matrixScoreOf, PRIORITY_FLAG_HEX } from "../task-colors.js";
import { staleWeeks, type QueueBand, type ViewItem } from "./model.js";
import type { DropBand } from "./useBoardDrag.js";

interface Props {
  queue: ViewItem[];
  now: Date;
  onToggle: (task: Task, done: boolean) => void;
  onOpen: (task: Task) => void;
  /** Opens the SnoozeMenu at the click point — snoozed items leave the view
   *  (they are parked; the Today view owns waking them). */
  onSnooze: (task: Task, pos: { x: number; y: number }) => void;
  /** Jump to the task's slot on the calendar (flash highlight). */
  onView: (task: Task) => void;
  /** Band currently hovered by a board-chip drag (release = file it there). */
  dropBand: DropBand | null;
  /** True while a board chip is being dragged — empty now/next/later bands
   *  materialize as drop targets so any band is reachable. */
  dragActive: boolean;
}

const BAND_ORDER: QueueBand[] = ["now", "next", "later", "unranked"];
const BAND_LABEL: Record<QueueBand, I18nKey> = {
  now: "priorities.band.now",
  next: "priorities.band.next",
  later: "priorities.band.later",
  unranked: "priorities.band.unranked",
};
/** Top picks get a numbered badge so "what do I start with" is one glance. */
const FOCUS_PICKS = 3;
/**
 * Rows rendered per band before a "show more".
 *
 * The queue is un-virtualized and every row is a `motion.li` with `layout`. On a
 * flat dataset it reached ~3,500 rows, and that un-virtualized list — not app JS
 * — was the residual cost of a Priorities board drop at large datasets: the
 * commit is deferred to transition priority, but the browser still has to style,
 * lay out and paint all of it. A band is a ranked queue; nobody works past the
 * first fifty, and the band header still shows the true total.
 */
const BAND_PAGE = 50;

/**
 * The decision queue: every in-range item in suggested execution order
 * (diagonal score → deadline → quick wins), grouped into the matrix's
 * diagonal bands. The top three open items carry focus badges.
 */
export const QueuePanel = memo(function QueuePanel({ queue, now, onToggle, onOpen, onSnooze, onView, dropBand, dragActive }: Props) {
  const { t } = useI18n();
  const locale = useAppLocale();
  const tf = useTimeFormat();

  // Per-band render cap. Deliberately NOT reset when `queue` changes identity:
  // it changes on every edit, and collapsing an expansion because the user
  // ticked a checkbox would be worse than keeping it.
  const [limits, setLimits] = useState<Partial<Record<QueueBand, number>>>({});

  // Focus ranks: position among open items, counted across bands.
  const ranks = new Map<string, number>();
  let rank = 0;
  for (const item of queue) {
    if (!item.done && item.band !== "unranked" && rank < FOCUS_PICKS) ranks.set(item.task.id, ++rank);
  }

  return (
    <Card className="overflow-hidden p-0">
      <h2 className="border-b bg-muted/40 px-3 py-2 text-sm font-medium">{t("priorities.queue.title")}</h2>
      {queue.length === 0 ? (
        <p className="px-3 py-3 text-sm text-muted-foreground">{t("priorities.queue.empty")}</p>
      ) : (
        BAND_ORDER.map((band) => {
          const items = queue.filter((i) => i.band === band);
          const limit = limits[band] ?? BAND_PAGE;
          const visible = items.length <= limit ? items : items.slice(0, limit);
          const droppable = band !== "unranked";
          // Empty bands surface as drop targets while a chip is in flight.
          if (items.length === 0 && !(dragActive && droppable)) return null;
          const isDropTarget = droppable && dropBand === band;
          return (
            <section
              key={band}
              {...(droppable ? { "data-queue-band": band } : {})}
              className={cn("transition-colors", isDropTarget && "bg-primary/10")}
            >
              <h3
                className={cn(
                  "flex items-center gap-2 border-b bg-muted/20 px-3 py-1 text-[calc(11px*var(--app-text-scale))] font-medium uppercase tracking-wider text-muted-foreground transition-colors",
                  isDropTarget && "bg-primary/15 text-primary",
                )}
              >
                {t(BAND_LABEL[band])}
                <span className="font-normal normal-case tracking-normal">{items.length}</span>
              </h3>
              {items.length === 0 && (
                <p className="border-b border-dashed px-3 py-2 text-xs text-muted-foreground/70">
                  {t("priorities.queue.dropHere")}
                </p>
              )}
              <ul className="flex flex-col divide-y divide-border text-sm">
                <AnimatePresence initial={false} presenceAffectsLayout={false}>
                  {visible.map((item) => (
                    <QueueRow
                      key={item.task.id}
                      item={item}
                      focusRank={ranks.get(item.task.id) ?? null}
                      now={now}
                      locale={locale}
                      hmDate={tf.hmDate}
                      stamp={tf.stamp}
                      onToggle={onToggle}
                      onOpen={onOpen}
                      onSnooze={onSnooze}
                      onView={onView}
                    />
                  ))}
                </AnimatePresence>
              </ul>
              {visible.length < items.length && (
                <ShowMoreFooter
                  shown={visible.length}
                  total={items.length}
                  onShowMore={() => setLimits((prev) => ({ ...prev, [band]: (prev[band] ?? BAND_PAGE) + BAND_PAGE }))}
                />
              )}
            </section>
          );
        })
      )}
    </Card>
  );
});

interface RowProps {
  item: ViewItem;
  focusRank: number | null;
  now: Date;
  locale: string;
  hmDate: (d: Date) => string;
  stamp: (iso: string) => string;
  onToggle: (task: Task, done: boolean) => void;
  onOpen: (task: Task) => void;
  onSnooze: (task: Task, pos: { x: number; y: number }) => void;
  onView: (task: Task) => void;
}

/** Memoized for the same reason as the board chips: `buildMatrixView`'s item
 *  cache keeps untouched `ViewItem`s identical across an edit, so only the row
 *  that actually changed re-renders. Its callbacks must stay stable (the route
 *  wraps them in `useCallback`). */
const QueueRow = memo(function QueueRow({ item, focusRank, now, locale, hmDate, stamp, onToggle, onOpen, onSnooze, onView }: RowProps) {
  const { t } = useI18n();
  const reduce = useReducedMotion();
  const task = item.task;
  // Same placed-gate as the board chips: continuous diagonal only when placed.
  const color = item.placed && item.pos
    ? matrixColorHex(matrixScoreOf(item.pos))
    : PRIORITY_FLAG_HEX[task.priority] || "#94a3b8";

  let dueText: string | null = null;
  let dueTone = "text-muted-foreground";
  if (!task.due_at) {
    dueText = t("priorities.dueNone");
  } else if (item.overdue) {
    const days = overdueAgeDays(task, now);
    dueText = days <= 0 ? t("priorities.overdueToday") : t("priorities.overdueBy", { days });
    dueTone = "text-red-600 dark:text-red-400 font-medium";
  } else {
    const due = fromIso(task.due_at);
    dueText = agendaDayLabel(
      due,
      now,
      locale,
      { today: t("today.relative.today"), tomorrow: t("today.relative.tomorrow") },
    ) + (task.all_day ? "" : ` · ${hmDate(due)}`);
  }

  return (
    <motion.li
      layout={!reduce}
      initial={reduce ? false : { opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={reduce ? { opacity: 0, transition: { duration: 0 } } : { opacity: 0, x: -16 }}
      transition={reduce ? { duration: 0 } : springs.smooth}
      className="grid grid-cols-[auto_auto_minmax(0,1fr)] items-center gap-x-2 gap-y-2 px-3 py-2 transition-colors hover:bg-accent/30 sm:flex sm:gap-2.5"
    >
      {task.is_event ? (
        <span role="img" className="grid h-4 w-4 shrink-0 place-items-center" aria-label={t("common.event")}>
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} aria-hidden />
        </span>
      ) : (
        <input
          type="checkbox"
          checked={item.done}
          onChange={(e) => onToggle(task, e.target.checked)}
          className="h-4 w-4 shrink-0 rounded border-input"
          aria-label={item.done ? t("tasks.markOpen") : t("tasks.markDone")}
        />
      )}
      {focusRank !== null ? (
        <motion.span
          initial={reduce ? false : { scale: 0.4, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={reduce ? { duration: 0 } : springs.bouncy}
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[calc(11px*var(--app-text-scale))] font-semibold text-white"
          style={{ background: color }}
          title={t("priorities.focusPick")}
        >
          {focusRank}
        </motion.span>
      ) : (
        <span className="mx-1.5 h-2 w-2 shrink-0 rounded-full" style={{ background: color }} aria-hidden />
      )}
      <div
        role="button"
        tabIndex={0}
        className="min-w-0 flex-1 cursor-pointer rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={() => onOpen(task)}
        onKeyDown={(e) => {
          if (e.key !== "Enter" && e.key !== " ") return;
          e.preventDefault();
          onOpen(task);
        }}
        title={t("tasks.tapToEdit")}
      >
        <div className="flex min-w-0 items-center gap-1.5">
          <span className={cn("min-w-0 break-words whitespace-normal sm:truncate", item.done && "text-muted-foreground line-through")}>
            {task.title || <em className="text-muted-foreground">{t("agenda.untitled")}</em>}
          </span>
          {task.recurrence_id && <Repeat className="h-3 w-3 shrink-0 text-muted-foreground" aria-label={t("agenda.recurring")} />}
          {item.pos?.urgencyAuto && <Clock className="h-3 w-3 shrink-0 text-muted-foreground/70" aria-label={t("matrix.autoUrgency.hint")} />}
          {item.stale && (
            <span
              className="shrink-0 rounded-full bg-amber-500/15 px-1.5 py-px text-[calc(10px*var(--app-text-scale))] font-medium leading-tight text-amber-600 dark:text-amber-400"
              title={t("priorities.stale.hint", { weeks: staleWeeks(task, now) })}
            >
              {t("priorities.stale.badge", { weeks: staleWeeks(task, now) })}
            </span>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-xs text-muted-foreground">
          <span className={dueTone}>{dueText}</span>
          {task.deadline_at && (
            <span className="flex items-center gap-1">
              <Flag className="h-3 w-3" aria-hidden />
              {stamp(task.deadline_at)}
            </span>
          )}
          {task.duration_minutes > 0 && <span>{formatDuration(task.duration_minutes)}</span>}
        </div>
      </div>
      {((!item.done && !task.is_event) || task.due_at) && (
        <div className="col-start-2 col-end-4 flex items-center justify-self-end gap-1 sm:col-auto sm:justify-self-auto">
          {!item.done && !task.is_event && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 shrink-0 p-0 sm:h-9 sm:w-auto sm:px-3"
              onClick={(e) => {
                e.stopPropagation();
                onSnooze(task, { x: e.clientX, y: e.clientY });
              }}
              title={t("agenda.snoozeTitle")}
              aria-label={t("agenda.snoozeTitle")}
            >
              <Clock className="h-4 w-4" />
            </Button>
          )}
          {task.due_at && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 shrink-0 p-0 sm:h-9 sm:w-auto sm:px-3"
              onClick={(e) => {
                e.stopPropagation();
                onView(task);
              }}
              title={t("agenda.showOnCalendar")}
              aria-label={t("agenda.showOnCalendar")}
            >
              <Calendar className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}
    </motion.li>
  );
});
